import React, { useState, Fragment } from 'react';
import {
  ErrorBoundary as BaseErrorBoundary,
  FallbackProps,
  ErrorBoundaryPropsWithRender,
} from 'react-error-boundary';
import { ContactBoundary, ReloadBoundary } from '@ui/core/errors';
import { captureException } from '@infrastructure/monitoring/sentry';
import { BrandProvider } from '@infrastructure/styling/BrandProvider';

export interface ErrorFallbackComponentProps extends FallbackProps {
  shouldShowRetry: boolean;
}

const ErrorFallbackComponent: React.FC<ErrorFallbackComponentProps> = ({
  resetErrorBoundary,
  shouldShowRetry,
}) => {
  return shouldShowRetry ? (
    <ReloadBoundary resetErrorBoundary={resetErrorBoundary} />
  ) : (
    <ContactBoundary />
  );
};

/**
 *
 * React error boundaries catch errors in 1) rendering, 2) lifecycle methods, 3) constructors of the
 * component tree below the boundary
 *
 * They cannot catch errors for: 1) event handlers, 2) async code, 3) SSR, 4) errors thrown in the boundary
 * itself (see useErrorBoundaryErrorHandler at the bottom)
 */
export type ErrorBoundaryProps = Pick<
  ErrorBoundaryPropsWithRender,
  'onError' | 'onReset'
> & {
  maxRetries?: number;
  ErrorFallback?: typeof ErrorFallbackComponent;
};

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  ErrorFallback = ErrorFallbackComponent,
  maxRetries = 2,
  onError = (error) => captureException(error),
  onReset,
  children, // what you want rendered when everything's working fine
}) => {
  const [retryCounter, setRetryCounter] = useState(0);

  const innerOnReset = () => {
    setRetryCounter((counter) => counter + 1);
    typeof onReset === 'function' && onReset();
  };

  // changing a `key` value cause to re-mount it and its children
  return (
    <BrandProvider>
      <BaseErrorBoundary
        fallbackRender={(fallbackProps) => (
          <ErrorFallback
            {...fallbackProps}
            shouldShowRetry={retryCounter < maxRetries}
          />
        )}
        onError={onError}
        onReset={innerOnReset}
      >
        <Fragment key={`retry-${retryCounter}`}>{children}</Fragment>
      </BaseErrorBoundary>
    </BrandProvider>
  );
};

export { ErrorBoundary };
